import angular from 'angular'

import { QuickKeyNodeType } from '../qk.constants'
import { navigate } from '../qk-navigation'
import { $document, $timeout } from '../../ng-compat/imports'
import template from './vd-quick-key-grid.html'

/**
 * Directive for rendering a grid of quick keys.
 *
 * @module vendQuickKeys.vdQuickKeyGrid
 */
angular.module('vendQuickKeys').directive('vdQuickKeyGrid', function () {
  return {
    restrict: 'E',
    scope: {
      rootNode: '=',
      inFolder: '=',
      masked: '=',
      layout: '=',
    },
    template,
    bindToController: true,
    controllerAs: 'qkGridCtrl',
    controller(this: $TSFixMe, $element, $scope) {
      let currentFocusPosition: $TSFixMe
      let currentFocusQuickKey: $TSFixMe
      function getQuickKeyElement(quickKey: $TSFixMe) {
        return angular.element(
          ($document[0] as Document).getElementById(`qk-${quickKey.node.id}`)!
        )
      }

      // @todo can move the navigation into the model?
      this._focusQuickKey = function (navData: $TSFixMe) {
        navData.fromPosition = navData.fromPosition || currentFocusPosition
        const navigationData = navigate(this.rootNode, navData, {
          includeBlanks: this.layout.editable,
        })

        if (navigationData.from) {
          const currFocusEl = getQuickKeyElement(navigationData.from)
          if (currFocusEl) {
            currFocusEl.removeAttr('tabindex')
          }
        }

        if (navigationData.to) {
          const quickKeyEl = getQuickKeyElement(navigationData.to)
          if (quickKeyEl.length) {
            quickKeyEl.attr('tabindex', 0)[0].focus()
          }
        }

        currentFocusPosition = navigationData.toPosition
        currentFocusQuickKey = navigationData.to

        // Mark the grid itself as unfocusable, the next tab stop will be the focussed quick key within - unless there
        // is no focusable quick key therein
        if (currentFocusQuickKey) {
          $element.attr('tabindex', -1)
        }
      }

      /**
       * Focus the appropriate quick key (either the first focusable or the last focussed) in the grid.
       *
       * @private
       * @method _focusGrid
       *
       * @param  {Position} [toPosition]
       *         Optional quick key position to explicitly focus - e.g. if the focus was triggered by clicking on a
       *         specific quick key and not just tabbing into the grid
       */
      this._focusGrid = function (toPosition = currentFocusPosition) {
        this._focusQuickKey({ toPosition })
      }
      $scope.$on(
        'vd-quick-keys:keyboard-navigate',
        (event: $TSFixMe, navData: $TSFixMe) => this._focusQuickKey(navData)
      )
      $scope.$on(
        'vd-quick-keys:focus-at-position',
        (event: $TSFixMe, toPosition: $TSFixMe) =>
          this._focusQuickKey({ toPosition })
      )
      $scope.$watch(
        'qkGridCtrl.masked',
        (newVal: $TSFixMe, oldVal: $TSFixMe) => {
          if (typeof newVal === 'boolean') {
            $element.toggleClass('vd-quick-key-grid--masked', newVal)
            if (oldVal && !newVal) {
              // Refocus the grid when the mask is removed (i.e. folder is closed)
              this._focusGrid()
            }
          }
        }
      )
      $scope.$watch('qkGridCtrl.layout.editable', (newVal: $TSFixMe) => {
        if (
          !newVal &&
          currentFocusQuickKey &&
          currentFocusQuickKey.type === QuickKeyNodeType.BLANK
        ) {
          currentFocusPosition = null
          currentFocusQuickKey = null
          $element.attr('tabindex', 0)
        }
      })

      $element.attr('tabindex', 0)
      let qkMouseDownData: $TSFixMe
      $scope.$on(
        'vd-quick-keys:mousedown-at-position',
        (event: $TSFixMe, position: $TSFixMe) => {
          qkMouseDownData = {
            time: Date.now(),
          }
        }
      )
      $scope.$on(
        'vd-quick-keys:drag-start',
        (event: $TSFixMe, quickKey: $TSFixMe) => {
          this.layout.draggedQuickKey = quickKey
          $element.addClass('vd-quick-key-grid--dragging')
        }
      )
      $scope.$on('vd-quick-keys:drag-end', (event: $TSFixMe) => {
        $element.removeClass('vd-quick-key-grid--dragging')
      })
      $scope.$on(
        'vd-quick-keys:drop',
        (event: $TSFixMe, quickKey: $TSFixMe, toPosition: $TSFixMe) => {
          $element.removeClass('vd-quick-key-grid--dragging')
          const quickKeyOrigin = this.layout.draggedQuickKey
          if (
            !this.layout.isValidDropDestination(quickKey) ||
            quickKeyOrigin.node.id === quickKey.node.id
          ) {
            return false
          }
          this.layout
            .processDragAndDrop({
              quickKeyOrigin,
              quickKeyDestination: quickKey,
            })
            .then(() =>
              $scope.$applyAsync(() => {
                this._focusQuickKey({ toPosition })
                this.layout.draggedQuickKey = null
              })
            )
        }
      )
      $element.on('focus', (event: $TSFixMe) => {
        event.preventDefault()

        // If there hasn't been a mousedown event on a quick key, or if the event occured over 50ms ago, then focus
        // the grid. Allows the immediate selection and dragging of a quick key when in edit mode.
        if (!qkMouseDownData || Date.now() - qkMouseDownData.time > 50) {
          this._focusGrid()
        }
      })
    },
    link(scope: $TSFixMe, element, attrs) {
      attrs.$addClass('vd-quick-key-grid')
      if (scope.qkGridCtrl.inFolder) {
        // Focus the first key in the folder (asynchronously to ensure the grid has completed rendering)
        $timeout(() => scope.qkGridCtrl._focusGrid())
      }
    },
  }
})
